import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import AuthActionPage from './pages/auth/AuthActionPage';
import MonitoringSetupPage from './pages/monitoring/MonitoringSetupPage';
import RealtimeDashboard from './pages/realtime/RealtimeDashboard';
import ReportPage from './pages/realtime/ReportPage';
import ProjectDashboard from './pages/realtime/ProjectDashboard';
import { ProductScopeProvider } from './contexts/ProductScope';
import NationalOdmDemo from './pages/demo/NationalOdmDemo';

import HomePage from './pages/home/HomePage';
import CustomPolygonDrawPage from './pages/areas/CustomPolygonDrawPage';
import StandardPolygonSelectPage from './pages/areas/StandardPolygonSelectPage';
import RoadLinksSelectPage from './pages/areas/RoadLinksSelectPage';
import ActivityAreaSelectPage from './pages/activities/ActivityAreaSelectPage';
import ActivityMapPage from './pages/activities/ActivityMapPage';
import ActivityPreviewPage from './pages/activities/ActivityPreviewPage';
import ActivityDashboard from './pages/demo/ActivityDashboard';
import OdmDashboard from './pages/demo/OdmDashboard';
import OdmSetupPage from './pages/odm/OdmSetupPage';
import OdmMapPage from './pages/odm/OdmMapPage';
import OdmPreviewPage from './pages/odm/OdmPreviewPage';
import FlowPage from './pages/flow/FlowPage';
import ApiKeysPreviewPage from './pages/api-keys-preview/ApiKeysPreviewPage';
import SettingsPage from './pages/settings/SettingsPage';
import FeedbackButton from './components/common/FeedbackButton';
import UpdateNotice from './components/common/UpdateNotice';
import ReadOnlyNotices from './components/common/ReadOnlyNotices';
import EntitledRoute from './components/common/EntitledRoute';
import SupportPage from './pages/support/SupportPage';
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper-200">
        <div className="flex flex-col items-center gap-4">
          <div className="tf-spinner tf-spinner-lg" />
          <p className="text-sm text-ink-muted">Loading…</p>
        </div>
      </div>
    );
  }

  return currentUser ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={currentUser ? <Navigate to="/" /> : <LoginPage />}
      />
      {/* Password links from invite/reset emails; works signed in or out. */}
      <Route path="/auth/action" element={<AuthActionPage />} />
      {/*
        Hidden design-preview route — fully faked, not linked from the nav.
        Used to explore the ideal API-key UX so the backend contract can be
        reverse-engineered from a known-good UI.
      */}
      <Route
        path="/preview/api-keys"
        element={<ApiKeysPreviewPage />}
      />
      {/* Settings: your account, and the organisation's team for editors (#562). */}
      <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
      <Route
        path="/settings/:tab"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      {/* Telcofy staff: open a customer organisation read-only (#557). */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      {/* Data coverage became the Status section of Home; old links land there. */}
      <Route
        path="/data-coverage"
        element={
          <ProtectedRoute>
            <Navigate to="/?section=status" replace />
          </ProtectedRoute>
        }
      />
      {/* The #562 sketch lived here; links to it now open the real page. */}
      <Route path="/preview/team" element={<Navigate to="/settings/team" replace />} />
      <Route
        path="/demo-national"
        element={<NationalOdmDemo />}
      />
      <Route
        path="/monitor-areas-select"
        element={
          <ProtectedRoute>
            <EntitledRoute product="realtime">
              <MonitoringSetupPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-polygon"
        element={
          <ProtectedRoute>
            <CustomPolygonDrawPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/polygon-select"
        element={
          <ProtectedRoute>
            <StandardPolygonSelectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/road-links"
        element={
          <ProtectedRoute>
            <RoadLinksSelectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activities"
        element={
          <ProtectedRoute>
            <EntitledRoute product="activities">
              <ActivityAreaSelectPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activities-map"
        element={
          <ProtectedRoute>
            <EntitledRoute product="activities">
              <ActivityMapPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-preview"
        element={
          <ProtectedRoute>
            <EntitledRoute product="activities">
              <ActivityPreviewPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-dashboard"
        element={
          <ProtectedRoute>
            <EntitledRoute product="activities">
              <ActivityDashboard />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/odm-area-select"
        element={
          <ProtectedRoute>
            <EntitledRoute product="odm">
              <OdmSetupPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/odm"
        element={
          <ProtectedRoute>
            <EntitledRoute product="odm">
              <OdmMapPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/odm-preview"
        element={
          <ProtectedRoute>
            <EntitledRoute product="odm">
              <OdmPreviewPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/odm-dashboard"
        element={
          <ProtectedRoute>
            <EntitledRoute product="odm">
              <OdmDashboard />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/flow"
        element={
          <ProtectedRoute>
            <EntitledRoute product="flow">
              <FlowPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/realtime"
        element={
          <ProtectedRoute>
            <EntitledRoute product="realtime">
              <RealtimeDashboard />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/realtime/project"
        element={
          <ProtectedRoute>
            <EntitledRoute product="realtime">
              <ProjectDashboard />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/realtime/report"
        element={
          <ProtectedRoute>
            <EntitledRoute product="realtime">
              <ReportPage />
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergency"
        element={
          <ProtectedRoute>
            <EntitledRoute product="emergency">
              <ProductScopeProvider product="emergency">
                <MonitoringSetupPage />
              </ProductScopeProvider>
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergency/dashboard"
        element={
          <ProtectedRoute>
            <EntitledRoute product="emergency">
              <ProductScopeProvider product="emergency">
                <RealtimeDashboard />
              </ProductScopeProvider>
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergency/project"
        element={
          <ProtectedRoute>
            <EntitledRoute product="emergency">
              <ProductScopeProvider product="emergency">
                <ProjectDashboard />
              </ProductScopeProvider>
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergency/report"
        element={
          <ProtectedRoute>
            <EntitledRoute product="emergency">
              <ProductScopeProvider product="emergency">
                <ReportPage />
              </ProductScopeProvider>
            </EntitledRoute>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Root surface: DaisyUI's theme attribute follows the same switch as the Tailwind tokens.
const Shell: React.FC = () => {
  const { isDark } = useTheme();
  return (
    <div className="min-h-screen bg-paper-200 font-sans" data-theme={isDark ? "mydark" : "mytheme"}>
      <AppRoutes />
      <FeedbackButton />
      <ReadOnlyNotices />
      <UpdateNotice />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Shell />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;